import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ArrowRight, CheckCircle } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&h=400&fit=crop&crop=center";

const Hero = ({ stats }) => {
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToCaseStudies = () => {
    const element = document.querySelector("#case-studies");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="hero-bg flex items-center pt-16" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="order-2 md:order-1"
          >
            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
              Scale Your{" "}
              <span className="gradient-text">Web & App Revenue</span>
              {" "}with Data-Driven Marketing
            </h1>

            {/* Subheading */}
            <p className="text-base text-[#8B949E] leading-relaxed mb-6 max-w-lg">
              Dubai's Performance Marketing Agency for Global Brands. Specializing in App UA, E-Commerce growth, and ROAS optimization across US, MENA & India.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mb-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="gradient-btn px-6 py-3 text-base font-semibold inline-flex items-center gap-2"
                data-testid="hero-cta-button"
              >
                Book Free Consultation
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToCaseStudies}
                className="px-6 py-3 text-base font-semibold border border-white/20 rounded-full hover:bg-white/5 transition-colors inline-flex items-center gap-2"
                data-testid="hero-secondary-button"
              >
                View Our Work
              </motion.button>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00C853]" />
                <span className="text-sm text-[#8B949E]">
                  <span className="text-white font-semibold">{stats?.projects_completed || "50+"}</span> Projects
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00C853]" />
                <span className="text-sm text-[#8B949E]">
                  <span className="text-white font-semibold">{stats?.average_roas || "3.5x"}</span> Avg ROAS
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00C853]" />
                <span className="text-sm text-[#8B949E]">
                  <span className="text-white font-semibold">{stats?.managed_spends || "$50M+"}</span> Media Spend
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 md:order-2 relative"
          >
            <div className="relative max-w-md mx-auto">
              {/* Gradient Orbs */}
              <div className="gradient-orb gradient-orb-blue w-40 h-40 -top-5 -right-5 absolute"></div>
              <div className="gradient-orb gradient-orb-green w-32 h-32 -bottom-5 -left-5 absolute"></div>
              
              {/* Image Container */}
              <div className="relative z-10 rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={HERO_IMAGE}
                  alt="Dubai Skyline - North Stack Performance Marketing"
                  className="w-full h-64 md:h-72 object-cover"
                  data-testid="hero-image"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1419] via-transparent to-transparent"></div>
              </div>

              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-4 -left-4 z-20 glass rounded-xl p-3 border border-white/10"
                data-testid="floating-stat-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#007AFF] to-[#00C853] flex items-center justify-center">
                    <span className="text-lg font-bold">+</span>
                  </div>
                  <div>
                    <div className="text-xl font-bold">
                      <CountUp end={107} duration={2.5} suffix="%" />
                    </div>
                    <div className="text-xs text-[#8B949E]">Orders Increase</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Stats Card 2 */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -top-3 -right-3 z-20 glass rounded-xl p-3 border border-white/10"
                data-testid="floating-stat-card-2"
              >
                <div className="flex items-center gap-2">
                  <div className="text-xl font-bold text-[#00C853]">
                    -<CountUp end={70} duration={2.5} suffix="%" />
                  </div>
                  <div className="text-xs text-[#8B949E]">CPO<br/>Reduction</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
